<template>
  <DsPageContent
    v-show="ready"
    class="page"
    @scroll="onScroll"
  >
    <div
      v-if="content"
      class="ds-container"
      :class="{ 'ds-height-full': !content.callToAction }"
    >
      <div class="ds-grid ds-height-full">
        <!-- back to top button for [screen small] -->
        <BackToTopButton
          v-if="!isScreenLarge"
          :aria-label="labels.backToTop || ''"
          :is-visible="isBackToTopButtonVisible"
        />
        <!-- page navigation for [screen large] -->
        <aside
          v-if="isScreenLarge"
          class="page__nav ds-grid__col ds-grid__col--12 ds-md-grid__col--3 sticky-top ds-scrollbar-hidden"
        >
          <DsLinkBack
            v-if="pageContent.breadcrumb && pageContent.breadcrumb.url"
            :text="pageContent.breadcrumb.title"
            :url="pageContent.breadcrumb.url"
            type="secondary"
          />
          <PageNavigation
            v-if="content.navigationItems && content.navigationItems.length"
            :items="content.navigationItems"
            :aria-label-navigation="labels.relatedPages || ''"
          />
        </aside>
        <!-- on page navigation -->
        <aside
          v-if="showOnPageNavigation"
          class="page__aside ds-grid__col grid__col--12 ds-md-grid__col--3 sticky-top ds-scrollbar-hidden"
        >
          <DsOnPageNavigation
            v-if="showDefaultLocaleContent.value"
            key="withDefaultLocalePageData"
            disposition="vertical"
            :items="onPageNavigationItems"
            :screen="screen"
            :title="labels.onThisPage || ''"
            :show-numbers="!!content.isChaptersNumbered"
            :update-route="false"
            :offset-scroll-top="onPageNavigationOffsetScrollTop"
            :initial-active-item="route.hash"
            :event-data="{ 'dropdown-opened': { setScrollTop: null } }"
            portal-target="header"
          />
          <DsOnPageNavigation
            v-else
            key="withPageData"
            disposition="vertical"
            :items="onPageNavigationItems"
            :screen="screen"
            :title="labels.onThisPage || ''"
            :show-numbers="!!content.isChaptersNumbered"
            :offset-scroll-top="onPageNavigationOffsetScrollTop"
            :initial-active-item="route.hash"
            :event-data="{ 'dropdown-opened': { setScrollTop: null } }"
            portal-target="header"
          />
        </aside>
        <!-- page main content -->
        <section
          class="page__main ds-grid__col ds-grid__col--12 ds-padding-y-48 ds-lg-padding-left-48"
          :class="[withoutOnPageNavigation ? 'ds-lg-grid__col--9' : 'ds-lg-grid__col--6']"
        >
          <!-- Display content fallback switch -->
          <template v-if="showDuoLocaleContent">
            <div class="page__main__info-block ds-sm-only-full-screen ds-md-only-full-screen">
              <p>{{ labelDisclaimer }}</p>
            </div>
            <div
              class="page__main__fallback-switch sticky-top ds-sm-only-full-screen ds-md-only-full-screen"
              :class="{ 'sticky-top--below-on-page-navigation': showOnPageNavigation && !isScreenLarge }"
            >
              <p>
                {{ labels.switchTo }}
                <button
                  type="button"
                  class="ds-button-link ds-button-link--primary"
                  @click="handleLanguageSwitch"
                >
                  <span>
                    {{ switchToLanguageName }}
                    <DsIconCore
                      category="arrows"
                      name="chevron-right-small"
                      class="ds-margin-left-8"
                    />
                  </span>
                </button>
              </p>
            </div>
          </template>
          <!-- breadcrumb link for [screen small] -->
          <DsLinkBack
            v-if="pageContent.breadcrumb && pageContent.breadcrumb.url && !isScreenLarge"
            :text="pageContent.breadcrumb.title"
            :url="pageContent.breadcrumb.url"
            type="secondary"
            class="ds-margin-bottom-24"
          />
          <!-- page content -->
          <h1
            v-if="content.title"
            class="ds-heading ds-margin-bottom-12"
          >
            {{ content.title }}
          </h1>
          <!-- page content - Intro text -->
          <RichText
            v-if="content.introText"
            :key="`intro-text-${content.contentLocale}`"
            :data="content.introText"
            :content-locale="content.contentLocale"
            :table-type="tableType"
          />
          <!-- page content - Cookie options  TODO: check when migrating cookie settings page-->
          <template v-if="showCookieSettings">
            <button
              type="button"
              class="ds-button ds-button--primary ds-button--green ds-margin-top-12"
              @click="handleCookieSettings"
            >
              Change Cookie Preferences
            </button>
            <!-- OneTrust Cookies List start -->
            <div id="ot-sdk-cookie-policy" />
            <!-- OneTrust Cookies List end -->
          </template>
          <!-- page content - Download -->
          <DsLink
            v-if="content.downloadDocument"
            class="ds-margin-y-24"
            :url="content.downloadDocument.url"
            :open-in-new-window="content.downloadDocument.openInNewWindow"
            :content="content.downloadDocument.title"
            download
            type="button"
            :icon-right="{ category: 'arrows', name: 'download' }"
          />
          <!-- page content - highlight block -->
          <div
            v-if="content.highlightText"
            class="page__main__highlight-block ds-margin-y-48"
          >
            <RichText
              :key="`highlight-text--${content.contentLocale}`"
              :data="content.highlightText"
              :content-locale="content.contentLocale"
            />
          </div>
          <!-- page content - chapters -->
          <Chapters
            v-if="content.chapters && content.chapters.length"
            :items="content.chapters"
            :show-numbers="!!content.isChaptersNumbered"
            :content-locale="content.contentLocale"
            :table-type="tableType"
          />
          <!-- page content - privacy form -->
          <div
            v-if="showPrivacyForm"
            class="ds-margin-y-48"
          >
            <PrivacyForm />
          </div>
          <!--  page content - Privacy policy cards  -->
          <div
            v-if="content.items && showPrivacySubpages"
            class="ds-grid ds-padding-top-48"
          >
            <div
              v-if="content.itemsTitle"
              class="ds-grid__col ds-grid__col--12 ds-grid__col--no-gutter ds-padding-bottom-24"
            >
              <h2 class="ds-heading">
                {{ content.itemsTitle }}
              </h2>
              <p
                v-if="content.itemsDescription"
                class="ds-text ds-padding-top-24"
              >
                {{ content.itemsDescription }}
              </p>
            </div>
            <div
              v-for="(item, index) in content.items"
              :key="`privacy-card-${index}`"
              class="ds-grid__col ds-grid__col--12 ds-md-grid__col--6 ds-lg-grid__col--6 ds-padding-bottom-24"
              :class="[
                { 'ds-padding-right-0': index % 2 !== 0 && !isScreenSmall },
                { 'ds-padding-left-0': index % 2 === 0 || isScreenSmall }
              ]"
            >
              <DsCardIcon
                v-if="item"
                :icon="item.icon"
                :title="item.title"
                :link="item.link"
                :text="item.text"
              />
            </div>
          </div>
        </section>
      </div>
    </div>
    <DsCallToAction
      v-if="content && content.callToAction"
      v-bind="content.callToAction"
    />
  </DsPageContent>
</template>

<script setup>
import kebabCase from 'lodash-es/kebabCase'
import { DsCallToAction, DsIconCore, DsLink, DsLinkBack, DsOnPageNavigation, DsPageContent, DsCardIcon } from 'awds'
import { onMounted } from 'vue'
import BackToTopButton from '../back-to-top-button/back-to-top-button.vue'
import RichText from '../../../core/components/RichText.vue'
import Chapters from '../../../core/components/Chapters.vue'
import PageNavigation from '../layouts/page-navigation/page-navigation.vue'
import PrivacyForm from '../../components/privacy-form.vue'

const { labels, fetchGlobalDataForContentType } = useMainGlobalData()
const { globalData } = useGlobalData()
const { locale } = useI18n()
const route = useRoute()
const { screen } = useScreen()
const { getLocaleLanguage, defaultLocale } = useLocales()
const { isScreenLarge, isScreenSmall } = useScreen()
const emit = defineEmits(['prevent-header-transition'])
const props = defineProps({
  pageData: {
    type: Object,
    default: null
  },
  pageDataDefaultLocale: {
    type: Object,
    default: null
  },
  showCookieSettings: {
    type: Boolean,
    default: false
  },
  showPrivacyForm: {
    type: Boolean,
    default: false
  },
  withoutOnPageNavigation: {
    type: Boolean,
    default: false
  },
  showPrivacySubpages: {
    type: Boolean,
    default: false
  }
})

const isBackToTopButtonVisible = ref(true)
const showDefaultLocaleContent = ref(false)
const showDuoLocaleContent = ref(false)
const ready = ref(false)

await fetchGlobalDataForContentType('PageLegalDetail')

const cookieConsentData = computed(() => {
  return globalData?.cookieConsentData?.items[0]
})

const pageContent = computed(() => {
  if (showDefaultLocaleContent.value) {
    // breadcrumb & navigationItems from current locale
    return {
      ...props.pageDataDefaultLocale,
      content: {
        ...props.pageDataDefaultLocale.content,
        navigationItems: props.pageData.content?.navigationItems
      },
      breadcrumb: props.pageData.breadcrumb
    }
  }
  return props.pageData
})

const content = computed(() => {
  return pageContent?.value?.content
})

const showOnPageNavigation = computed(() => {
  return pageContent.value.content?.chapters?.length > 3 && !props.withoutOnPageNavigation
})

const onPageNavigationItems = computed(() => {
  return content?.value.chapters
    ?.filter((chapter) => chapter.title)
    .map((chapter) => ({
      label: chapter?.title,
      slug: kebabCase(chapter?.title)
    }))
})

const switchToLanguageName = computed(() => {
  return getLocaleLanguage(showDefaultLocaleContent.value ? locale.value : defaultLocale)
})

const labelDisclaimer = computed(() => {
  const languageName = getLocaleLanguage(content.value.contentLocale) || switchToLanguageName.value
  return labels.value.disclaimerLegalTranslationService.replace('{languageName}', languageName)
})

const onPageNavigationOffsetScrollTop = computed(() => {
  return props.pageDataDefaultLocale && props.pageData ? 76 : 0 // 76px height of language fallback-switch element
})
const tableType = computed(() => {
  switch (true) {
    case props.showCookieSettings:
    case props.withoutOnPageNavigation:
      return 'primary'
    default:
      return ''
  }
})

onBeforeMount(() => {
  // Determine if entry has content
  const hasChapters = props.pageData?.content?.chapters?.[0]?.title
  const entryHasContent = !!props.pageData?.content?.title && (!!props.pageData?.content?.introText || !!hasChapters)

  showDefaultLocaleContent.value = entryHasContent ? false : !!props.pageDataDefaultLocale
  showDuoLocaleContent.value = entryHasContent && !!props.pageDataDefaultLocale

  ready.value = true
})

onMounted(() => {
  if (!showOnPageNavigation.value && route.hash && typeof window !== 'undefined') {
    const dataId = route.hash.replace('#', '')
    const element = document.querySelector([`[data-id="${dataId}"]`])

    window.scrollTo({
      left: 0,
      top: element.offsetTop,
      behavior: 'smooth'
    })
  }
})

const onScroll = ({ currentPosition }) => {
  // button becomes visible after the threshold
  const threshold = typeof window !== 'undefined' ? window.screen.height : 0
  isBackToTopButtonVisible.value = currentPosition >= threshold
}

const handleLanguageSwitch = (e) => {
  // remove focus from switch button
  e.target.blur()
  // emit event used in layouts
  emit('prevent-header-transition')
  showDefaultLocaleContent.value = !showDefaultLocaleContent.value
}

const handleCookieSettings = () => {
  window?.OneTrust?.ToggleInfoDisplay()
}
</script>

<style lang="scss" scoped>
@import 'awds/foundation-vars.scss';
@import 'awds/foundation-mixins.scss';

.page {
  animation: animate-page-entry 500ms cubic-bezier(0.7, 0, 0.84, 0);

  &__main {
    background-color: $color-white;

    &__info-block {
      background-color: $color-grey-100;
      margin-top: -$spacing-24;
      padding: $spacing-24 $spacing-24 $spacing-0 $spacing-24;
      width: 100%;
    }

    &__highlight-block {
      background-color: $color-grey-100;
      padding: $spacing-24;
      width: 100%;
    }

    &__fallback-switch {
      background-color: $color-grey-100;
      margin-bottom: $spacing-24;
      padding: $spacing-24;
    }
  }

  &__aside {
    padding: $spacing-0;
    z-index: $z-index-1;
  }

  &__nav {
    padding: $spacing-0;
  }

  @include screen('large') {
    background: linear-gradient(90deg, $color-grey-100 50%, $color-white 50%);

    &__nav {
      height: calc(100vh - #{$height-header});
      order: 1;
      overflow-y: scroll;
      padding: $spacing-48 $spacing-24 0 $spacing-24;

      // Touch devices cannot calculate vh properly
      @media (hover: none) {
        height: calc(75vh - #{$spacing-48} - #{$height-header});
      }

      &:nth-child(1) {
        padding-left: $spacing-12;
      }
    }

    &__main {
      order: 2;

      &__info-block {
        margin-top: -$spacing-48;
      }
    }

    &__aside {
      height: calc(100vh - #{$height-header});
      order: 3;
      overflow-y: scroll;
      padding: $spacing-48 $spacing-24 0 $spacing-24;

      // Touch devices cannot calculate vh properly
      @media (hover: none) {
        height: calc(75vh - #{$spacing-48} - #{$height-header});
      }
    }
  }
}

// Override One Trust styles
:deep(.ot-sdk-cookie-policy) {
  section {
    margin-bottom: $spacing-48 !important;
  }

  #cookie-policy-description,
  .ot-sdk-cookie-policy-group-desc {
    font-size: $font-size !important;
    font-weight: $font-weight !important;
    line-height: $line-height !important;
    -webkit-font-smoothing: antialiased !important;
    -moz-osx-font-smoothing: grayscale !important;
  }

  #cookie-policy-description {
    margin-bottom: $spacing-48 !important;
  }

  .ot-sdk-cookie-policy-group {
    font-size: $font-size-large !important;
    font-weight: $font-weight-bold !important;
    line-height: $line-height-large !important;
    margin-bottom: $spacing-12 !important;
    -webkit-font-smoothing: antialiased !important;
    -moz-osx-font-smoothing: grayscale !important;
  }
}

@keyframes animate-page-entry {
  0% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
}
</style>
